import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { auth, firestore } from "../firebase";
import {
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { LogIn } from "lucide-react";
import Notifikasi from "../components/Notifikasi";

export default function Navbar() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownDesktopRef = useRef(null);
  const dropdownMobileRef = useRef(null);

  // AUTH STATE 
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userDoc = await getDoc(doc(firestore, "Users", currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setRole(data.role || "user");
          
          // Simpan username ke state agar bisa diakses
          // Kita simpan ke objek user sementara dengan cara menambahkan property baru
          setUser(prev => ({ ...prev, username: data.username }));
        }
      } else {
        setRole(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // LOCK SCROLL (STABLE) 
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileMenuOpen]);

  // CLOSE PROFILE OUTSIDE CLICK 
  useEffect(() => {
    function handleClickOutside(event) {
      const inDesktop = dropdownDesktopRef.current?.contains(event.target);
      const inMobile = dropdownMobileRef.current?.contains(event.target);
      if (!inDesktop && !inMobile) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  // ACTIONS
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/dashboard");
  };

  return (
    <>
      <nav className="bg-emerald-600 text-white shadow-lg fixed top-0 left-0 w-full z-50">
        <div className="max-w-7xl mx-auto relative flex items-center px-6 py-3">

          {/* garis tiga menu */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden relative w-10 h-10 flex items-center justify-center"
          >
            <span
              className={`absolute h-[2px] w-6 bg-white transition-all duration-300 ${
                mobileMenuOpen ? "rotate-45" : "-translate-y-1"
              }`}
            />
            <span
              className={`absolute h-[2px] w-6 bg-white transition-all duration-300 ${
                mobileMenuOpen ? "-rotate-45" : "translate-y-1"
              }`}
            />
          </button>

          {/* TITLE */}
          <h1 className="absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0 text-lg font-semibold">
            Monitoring Lingkungan Ruang Kelas
          </h1>

          {/* DESKTOP MENU */}
          <div className="hidden md:flex gap-4 items-center ml-auto">
            <button
              onClick={() => navigate(`/dashboard`)}
              className="bg-white text-green-700 px-4 py-2 rounded-full font-medium hover:text-emerald-400"
            >
              Dashboard
            </button>

            {(role === "user" || role === "admin") && (
              <button
                onClick={() => navigate("/histori")}
                className="border border-white px-4 py-2 rounded-full font-medium hover:bg-emerald-500"
              >
                Data Historis
              </button>
            )}
            
            {role === "admin" && (
              <button
                onClick={() => navigate("/admin/config")}
                className="border border-white px-4 py-2 rounded-full font-medium hover:bg-emerald-500"
              >
                Konfigurasi Sistem
              </button>
            )}

            {role === "teknisi" && (
              <button
                onClick={() => navigate("/teknisi/historiteknis")}
                className="border border-white px-4 py-2 rounded-full font-medium hover:bg-emerald-500"
              >
                Historis Teknis
              </button>
            )}
          
            {/* <Notifikasi /> */}

            {/* PROFILE DESKTOP */}
            {!user ? (
              <>
                <button 
                  onClick={() => navigate("/login")}
                  className="flex items-center gap-2 px-6 py-2.5 transition-all text-sm text-white font-medium hover:text-emerald-100"
                >
                  <LogIn className="w-4 h-4" />
                  Masuk
                </button>
              </>
            ) : (
              <>
                <div className="relative" ref={dropdownDesktopRef}>
                  <img
                    src={
                      user.photoURL ||
                      `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=random`
                    }
                    alt="User Avatar"
                    className="h-10 w-10 rounded-full border-2 border-white cursor-pointer hover:border-gray-300 transition"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  />

                  <div
                    className={`absolute right-0 mt-2 w-75 bg-white text-gray-800 rounded-lg shadow-lg border z-50 transform transition-all duration-300 ease-out
                    ${dropdownOpen
                      ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                      : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                    }`}
                  >
                    <div className="px-4 py-2 border-b">
                      <p className="font-semibold">{user.displayName || user.username || "User"}</p>
                      <p className="text-xs text-gray-500 capitalize">Role: {role || "user"}</p>
                    </div>

                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-b-lg"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
 
          {/* PROFILE MOBILE */}
          {user && (
            <div className="md:hidden ml-auto flex items-center gap-3">
              {/* <Notifikasi /> */}

              <div className="md:hidden ml-auto relative" ref={dropdownMobileRef}>
                <img
                  src={
                    user.photoURL ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=random`
                  }
                  alt="avatar"
                  className="h-9 w-9 rounded-full border-2 border-white cursor-pointer"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                />
  
                <div
                  className={`absolute right-0 mt-2 w-75 bg-white text-gray-800 rounded-lg shadow-lg border
                  transform transition-all duration-300 origin-top-right
                  ${dropdownOpen
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
                  }`}
                >
                  <div className="px-4 py-2 border-b">
                    <p className="font-semibold">{user.displayName || user.username || "User"}</p>
                    <p className="text-xs text-gray-500">Role: {role || "user"}</p>
                  </div>
  
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* SPACER AGAR KONTEN TIDAK KETUTUP NAVBAR */}
      <div className="h-[60px]" />
 
      {/* MOBILE MENU DROPDOWN */}
      <div className={`md:hidden fixed top-[60px] left-0 w-full h-[calc(100vh-60px)] bg-emerald-600 text-white z-40 transition-all duration-500
        ${mobileMenuOpen
          ? "opacity-100 translate-y-0"
          : "opacity-0 -translate-y-5 pointer-events-none"
        }`}
      >
        <div className="px-8 py-6 flex flex-col">
          <button
            onClick={() => { 
              navigate("/dashboard"); 
              setMobileMenuOpen(false); 
            }}
            className="py-4 border-b border-white/20 text-left"
          >
            Dashboard
          </button>
 
          {(role === "user" || role === "admin") && (
            <button
              onClick={() => {
                navigate("/histori"); 
                setMobileMenuOpen(false); 
              }}
              className="py-4 border-b border-white/20 text-left"
            >
              Data Historis
            </button>
          )}

          {role === "admin" && (
            <button
              onClick={() => {
                navigate("/admin/config");
                setMobileMenuOpen(false); 
              }}
              className="py-4 border-b border-white/20 text-left"
            >
              Konfigurasi Sistem
            </button>
          )}

          {role === "teknisi" && (
            <button
              onClick={() => {
                navigate("/teknisi/historiteknis"); 
                setMobileMenuOpen(false); 
              }}
              className="py-4 border-b border-white/20 text-left"
            >
              Historis Teknis
            </button>
          )}

          {!user && (
            <button
              onClick={()=>{
                navigate("/login");
                setMobileMenuOpen(false);
              }}
              className="py-4 border-b border-white/20 text-left"
            >
              Masuk
            </button>
          )}
        </div>
      </div>
    </>
  );
}